import { PlusIcon } from '@heroicons/react/16/solid';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNotificationStore } from '../../../components/notificationStore';

const WorkNotesForm = ({ requestId, readOnly = false }) => {
	const { showSuccess, showError } = useNotificationStore();
	const [workNotes, setWorkNotes] = useState('');
	const [isEditting, setIsEditing] = useState(false);
	const getNotesQuery = useQuery({
		queryKey: ['request-notes', requestId],
		queryFn: async () => {
			const { data } = await axios.get(`/requests/${requestId}/notes`);
			return data;
		},
		enabled: !!requestId,
	});
	const notesMutation = useMutation({
		mutationFn: async () => {
			await axios.post(`/requests/${requestId}/notes`, {
				note: workNotes,
			});
		},
		onSuccess: () => {
			showSuccess('Work notes saved successfully');
			setIsEditing(false);
			getNotesQuery.refetch();
		},
		onError: (/*error*/) => {
			showError('Failed to save work notes');
		},
	});

	const handleToggleForm = () => {
		setIsEditing(!isEditting);
		setWorkNotes('');
	};

	const handleCancel = () => {
		setIsEditing(false);
		// set to latest existing note
		if (getNotesQuery.data && getNotesQuery.data.length > 0) {
			setWorkNotes(getNotesQuery.data[getNotesQuery.data.length - 1].note);
		} else {
			setWorkNotes('');
		}
	};

	const handleSave = () => {
		notesMutation.mutate();
	};

	return (
		<div className="p-4 bg-white rounded">
			<h3>
				<strong>Work Notes</strong>
			</h3>
			{isEditting ? (
				<textarea
					className="w-full my-2 textarea textarea-bordered"
					rows={10}
					value={workNotes}
					onChange={({ target }) => setWorkNotes(target.value)}
					placeholder="Enter work notes here..."
					readOnly={readOnly}
				/>
			) : (
				<div className="flex-1 overflow-y-auto border max-h-96">
					{getNotesQuery.isPending && (
						<p className="flex-1">Loading notes...</p>
					)}
					{getNotesQuery.isError && (
						<p className="text-red-500">Error loading notes</p>
					)}
					{getNotesQuery.isFetched && getNotesQuery.data?.length === 0 && (
						<p>No notes found. Click the + to add one.</p>
					)}
					{getNotesQuery.isFetched && getNotesQuery.data?.length > 0 && (
						<div className="w-full">
							{getNotesQuery.data.map(({ note, coord, created_at }, index) => (
								<div key={index} className="p-2 border-b last:border-b-0">
									<p className="text-sm">{note}</p>
									<p className="text-right">
										<em className="text-xs text-gray-500">
											{coord
												? `${coord.name_first} ${coord.name_last}`
												: 'System Update'}
											,{' '}
											{created_at
												? new Date(created_at).toLocaleString()
												: 'No date available'}
										</em>
									</p>
								</div>
							))}
						</div>
					)}
				</div>
			)}
			<div className="flex items-center justify-between mt-4">
				{isEditting ? (
					<>
						<button className="btn btn-sm" onClick={handleCancel}>
							Cancel
						</button>
						<button
							className="btn btn-primary btn-sm"
							disabled={isEmpty(workNotes) || notesMutation.isPending}
							onClick={handleSave}
						>
							Save
						</button>
					</>
				) : (
					<>
						<div></div>
						<button
							className="btn btn-sm btn-primary"
							onClick={handleToggleForm}
						>
							<PlusIcon className="w-6 h-7" />
						</button>
					</>
				)}
			</div>
		</div>
	);
};

WorkNotesForm.propTypes = {
	requestId: PropTypes.number,
	readOnly: PropTypes.bool,
};

export default WorkNotesForm;
